import { useEffect, useRef } from 'react';
import { getAuthToken } from '../../utils/authToken';

/**
 * Hold the shared socket open while this page is mounted, and keep its handshake credential fresh.
 *
 * socket.io reuses the same `auth` object for every handshake, so a token captured once at mount goes stale.
 * Re-stamping it on each reconnect attempt means a transport outage (dyno restart, network drop) that spans a
 * session renewal reconnects with the NEW token instead of retrying for ever with the old one. Weekly.jsx does
 * the same.
 *
 * What this does NOT cover, deliberately: a token the SERVER rejects. That arrives as a CONNECT_ERROR packet, and
 * socket.io-client treats it as fatal — socket.destroy() → manager._close() sets `skipReconnect = true`
 * (manager.js:314), so `reconnectionAttempts: Infinity` stops applying and `reconnect_attempt` never fires again.
 * Only a fresh socket.connect() clears that flag. Recovering from an expired JWT therefore needs a deliberate
 * re-connect, which this hook does not yet do; the page falls back to its 60-second poll.
 *
 * `onOutage` fires once per outage rather than once per retry, and re-arms on the next successful connect. It is
 * only reached through `connect_error`, so a plain `disconnect` (including another page calling
 * socket.disconnect()) is currently silent.
 *
 * The socket is shared with the voice feeds and the softphone, which is why the page disconnects it on unmount —
 * whichever page is open owns it.
 */
export default function useOwnedSocket(socket, onOutage) {
    // Kept in a ref so a caller can pass an inline arrow without the effect re-running — that would disconnect and
    // reconnect the shared socket on every render.
    const outageRef = useRef(onOutage);
    outageRef.current = onOutage;

    useEffect(() => {
        if (!socket) return undefined;
        const manager = socket.io;
        let warned = false;

        const refreshToken = () => { socket.auth = { token: getAuthToken() }; };
        const onConnect = () => { warned = false; };
        const onConnectError = (err) => {
            console.warn('[missed-calls] socket connect failed:', err && err.message);
            if (warned) return;
            warned = true;
            if (outageRef.current) outageRef.current(err);
        };

        refreshToken();
        if (!socket.connected) socket.connect();
        socket.on('connect', onConnect);
        socket.on('connect_error', onConnectError);
        if (manager && typeof manager.on === 'function') manager.on('reconnect_attempt', refreshToken);

        return () => {
            socket.off('connect', onConnect);
            socket.off('connect_error', onConnectError);
            if (manager && typeof manager.off === 'function') manager.off('reconnect_attempt', refreshToken);
            socket.disconnect();
        };
    }, [socket]);
}
